/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { BRAND_LOGOS, type BrandKey, type BrandLogoAsset } from './brands';

interface BrandLogoProps {
    /** Which family brand to render. See `./brands.ts` for the map and `resolveBrandKey`. */
    brand: BrandKey;
    className?: string;
    /**
     * Hides the image from the accessibility tree, for use inside a link or button that already has
     * its own label. No call site yet — the PDP renders the mark bare and needs the accessible name
     * — but it mirrors the payment-logo components and is covered by the `Decorative` story.
     */
    decorative?: boolean;
}

/**
 * One of the 8 SRAM-family brand marks.
 *
 * Rendered through `<img>`, so the SVG cannot inherit `currentColor` — recolour per instance with a
 * CSS filter, as `apple-pay-logo.tsx` does.
 */
export default function BrandLogo({ brand, className, decorative = false }: BrandLogoProps) {
    const { t } = useTranslation('common');
    // Annotated, not inferred: `as const satisfies` makes this a union of literal types where only
    // the SRAM member has `filterClass`, so destructuring it off the union does not type-check.
    const { src, name, width, height, filterClass }: BrandLogoAsset = BRAND_LOGOS[brand];
    // `defaultValue` rather than `t(key) || name` — i18next returns the key
    // itself for a miss, so a `||` fallback can never fire.
    const label = t(`brandLogo.${brand}`, { defaultValue: name });

    return (
        <img
            src={src}
            alt={decorative ? '' : label}
            {...(decorative ? { 'aria-hidden': true } : {})}
            // A style, not `width`/`height` attributes — those parse as integers, and truncating
            // Truvativ's 64x8.3 to 64x8 skews it by ~4%.
            style={{ aspectRatio: `${width} / ${height}` }}
            // `filterClass` before `className` so a caller can override the recolouring.
            className={cn('h-4 w-auto object-contain', filterClass, className)}
        />
    );
}
